import type { TOC } from '@ember/component/template-only';
import { Text } from 'tio-ui/components/typography';
import { PhCheckCircle, PhHourglassHigh } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';

/**
 * BenefitOpportunity type representing an employer benefit
 */
export interface BenefitOpportunity {
  id: string;
  title: string;
  description: string;
  value: string;
  deadline?: string;
  enrolled: boolean;
  eligible: boolean;
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
}

/**
 * BenefitCard - Displays an employer benefit opportunity
 *
 * Shows benefit enrollment status, value, and optional deadline.
 * Enrolled benefits are highlighted with a ring indicator.
 *
 * @example
 * ```gts
 * <BenefitCard @benefit={{this.benefit}} />
 * ```
 */
interface BenefitCardSignature {
  Args: {
    /** The benefit opportunity data to display */
    benefit: BenefitOpportunity;
  };
  Element: HTMLDivElement;
}

const BenefitCard: TOC<BenefitCardSignature> = <template>
  {{! template-lint-disable no-bare-strings }}
  <div
    class="flex items-start gap-4 p-4 bg-gray-50 rounded-xl
      {{if @benefit.enrolled 'ring-2 ring-emerald-200 dark:ring-emerald-700'}}"
    role="article"
    aria-label="{{@benefit.title}}: {{@benefit.description}}"
    ...attributes
  >
    <div
      class="p-2.5 rounded-lg
        {{if
          @benefit.enrolled
          'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-400'
          'bg-gray-200 text-gray-500'
        }}"
    >
      {{#let @benefit.icon as |BenefitIcon|}}
        <BenefitIcon @size={{22}} />
      {{/let}}
    </div>
    <div class="flex-1 min-w-0">
      <div class="flex items-center gap-2 mb-1">
        <span class="font-semibold text-gray-900 dark:text-white">{{@benefit.title}}</span>
        {{#if @benefit.enrolled}}
          <PhCheckCircle
            @size={{16}}
            class="text-emerald-500 dark:text-emerald-400"
            aria-label="Enrolled"
          />
        {{/if}}
      </div>
      <Text @intent="muted" class="text-sm mb-2">{{@benefit.description}}</Text>
      <div class="flex items-center justify-between">
        <span class="text-emerald-600 dark:text-emerald-400 font-semibold">{{@benefit.value}}</span>
        {{#if @benefit.deadline}}
          <span class="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
            <PhHourglassHigh @size={{12}} aria-hidden="true" />
            <span>Expires {{@benefit.deadline}}</span>
          </span>
        {{/if}}
      </div>
    </div>
  </div>
</template>;

export default BenefitCard;
